import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { ValidationErrors, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { findLastIndex, has, isNumber, isObject } from 'lodash-es';
import { catchError, filter, finalize, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { FileService, PermissionService } from '@ipg/ngx-frontend-toolset';
import { PermissionSyncService } from '../../../../services/permission-sync.service';
import { MediaContext } from '../../../../models/media-context.model';
import {
  AudioActionValue,
  Interaction,
  InteractionAction,
  InteractionActionType,
  InteractionQuestion,
} from '../../../../models/interactions.model';
import { MediaItem, MediaResponse } from '../../../../models/media.model';
import { AudioPlayerService } from '../../../../services/audio-player.service';
import { MediaService } from '../../../../services/media.service';
import { ConfirmHandler } from '../../../dialog/dialog.model';
import { DialogService } from '../../../dialog/dialog.service';
import { InteractionSelectionDialogService } from '../../../interaction-selection-dialog/services/interaction-selection-dialog.service';
import { MediaPickerDialogService } from '../../../media-picker-dialog/services/media-picker-dialog.service';
import { InteractionEditorStateService } from '../../services/interaction-editor-state.service';
import { AudioActionComponent } from '../actions/audio-action/audio-action.component';
import { GotoInteractionActionComponent } from '../actions/goto-interaction-action/goto-interaction-action.component';
import { GotoQuestionActionComponent } from '../actions/goto-question-action/goto-question-action.component';
import { ImageActionComponent } from '../actions/image-action/image-action.component';
import { NotificationSmsActionComponent } from '../actions/notification-sms-action/notification-sms-action.component';
import { SpeakActionComponent } from '../actions/speak-action/speak-action.component';
import { StopActionComponent } from '../actions/stop-action/stop-action.component';
import { GotoQuestionDialogComponent } from '../dialogs/goto-question-dialog/goto-question-dialog.component';
import {
  ImageActionDialogComponent,
  ImageActionDialogResult,
} from '../dialogs/image-action-dialog/image-action-dialog.component';
import {
  NotificationSmsDialogComponent,
  NotificationSMSDialogResult,
} from '../dialogs/notification-sms-dialog/notification-sms-dialog.component';

type EditableAction = Exclude<InteractionAction, { type: 'stop' }>;

@Component({
  selector: 'app-action-list',
  imports: [
    CdkDrag,
    CdkDropList,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    SpeakActionComponent,
    AudioActionComponent,
    GotoQuestionActionComponent,
    GotoInteractionActionComponent,
    NotificationSmsActionComponent,
    StopActionComponent,
    ImageActionComponent,
  ],
  standalone: true,
  templateUrl: './action-list.component.html',
  styleUrl: './action-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block h-full',
  },
})
export class ActionListComponent implements OnDestroy {
  private readonly _dialog = inject(DialogService);
  private readonly _fileService = inject(FileService);
  private readonly _audioPlayerService = inject(AudioPlayerService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _mediaService = inject(MediaService);
  private readonly _interactionEditorState = inject(InteractionEditorStateService);
  private readonly _interactionSelectionDialog = inject(
    InteractionSelectionDialogService
  );
  private readonly _mediaPickerDialog = inject(MediaPickerDialogService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _permissionSync = inject(PermissionSyncService);

  private readonly _isImportingMedia = signal(false);

  protected readonly questions = computed(() => {
    const interaction = this._interactionEditorState.interaction();

    if (interaction) {
      return interaction.questions;
    }

    return [];
  });

  protected readonly disallowedTypes = ['goto-interaction', 'goto-question', 'stop'];

  protected readonly currentTrackId = computed(
    () => this._audioPlayerService.currentTrack()?.id
  );

  protected readonly isPlaying = computed(
    () => this._audioPlayerService.state().playing
  );

  public readonly actions = input<InteractionAction[]>([]);
  public readonly dropListId = input.required<string>();
  public readonly clientId = input<string | null>(null);

  public readonly disableControls = input(false, { transform: booleanAttribute });

  ngOnDestroy() {
    this._audioPlayerService.stop();
  }

  handleActionDropped(
    event: CdkDragDrop<
      InteractionAction[],
      unknown,
      InteractionAction | InteractionActionType
    >
  ) {
    if (this.disableControls()) {
      console.warn('Unable to sort action. Editor is locked');
      return;
    }

    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;
    const totalActions = event.container.data.length;

    if (this.isInteractionAction(event.item.data)) {
      if (previousIndex === currentIndex) {
        return;
      }

      const data = event.item.data;
      const isSameContainer = event.container === event.previousContainer;

      const isBlockingItem = this.disallowedTypes.includes(event.item.data.type);

      const lastBlockedIndex = findLastIndex(event.container.data, (action) =>
        this.disallowedTypes.includes(action.type)
      );

      const previousState = this._interactionEditorState.interaction();

      if (!previousState) {
        console.warn('No interaction state found.');

        return;
      }

      const updatedActions = this.reorderActions(
        previousState.actions,
        data.answerId,
        previousIndex,
        currentIndex
      );

      this._interactionEditorState.setState({
        ...previousState,
        actions: updatedActions,
      });

      if (isBlockingItem && currentIndex < totalActions - 1) {
        this._dialog
          .confirm({
            title: 'Actie verplaatsen?',
            message:
              'Als je deze actie omhoog verplaatst, worden alle onderliggende acties uit de lijst verwijderd. Weet je zeker dat je deze wijziging wilt doorvoeren? Deze actie kan niet ongedaan worden gemaakt',
            confirmText: 'Doorgaan',
            cancelText: 'Annuleren',
            onConfirm: () => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'sort',
                id: data._id,
                index: currentIndex,
              });

              return this._interactionEditorState.status$.pipe(
                filter((state) => state === 'idle'),
                take(1)
              );
            },
          })
          .afterClosed()
          .subscribe((confirmed) => {
            if (!confirmed) {
              this._interactionEditorState.setState(previousState);
            }
          });

        return;
      }

      if (
        !isBlockingItem &&
        lastBlockedIndex > -1 &&
        currentIndex >= lastBlockedIndex
      ) {
        this._dialog
          .alert({
            title: 'Actie verplaatsen mislukt',
            message:
              'Het is niet toegestaan om deze actie onder een geblokkeerde actie te plaatsen',
            confirmText: 'OK',
          })
          .afterClosed()
          .subscribe(() => {
            this._interactionEditorState.setState(previousState);
          });

        return;
      }

      if (isSameContainer) {
        this._interactionEditorState.emitEvent({
          entity: 'actions',
          action: 'sort',
          id: data._id,
          index: currentIndex,
        });
      }

      return;
    }

    let reason: string | null = null;

    if (
      this.disallowedTypes.includes(event.item.data) &&
      this.hasBlockingItem(event.container.data)
    ) {
      reason =
        'Het is niet toegestaan om nog een geblokkeerde actie toe te voegen. Er bestaat al een geblokkeerde actie in de lijst.';
    } else if (this.hasBlockingItem(event.container.data, currentIndex)) {
      reason =
        'Het is niet toegestaan om deze actie onder een geblokkeerde actie te plaatsen';
    }

    if (reason) {
      this._dialog.alert({
        title: 'Actie toevoegen mislukt',
        message: reason,
        confirmText: 'OK',
      });

      return;
    }

    this.handleActionAddDialog(event.item.data, currentIndex, totalActions);
  }

  isInteractionAction(
    value: InteractionAction | InteractionActionType
  ): value is InteractionAction {
    return isObject(value) && has(value, ['_id']);
  }

  reorderActions(
    actions: InteractionAction[],
    answerId: string,
    fromIndex: number,
    toIndex: number
  ) {
    const relatedActions = actions.filter((action) => action.answerId === answerId);
    const preservedActions = actions.filter((action) => action.answerId !== answerId);

    const copiedActions = [...relatedActions];
    moveItemInArray(copiedActions, fromIndex, toIndex);

    const updatedActions = copiedActions.map((action, index) => ({
      ...action,
      index,
    }));

    return [...preservedActions, ...updatedActions];
  }

  hasBlockingItem(items: InteractionAction[], toIndex?: number): boolean {
    const slice = toIndex != null ? items.slice(0, toIndex) : items;

    return slice.some((item) => this.disallowedTypes.includes(item.type));
  }

  handleAudioPlayback({ label, fileId }: AudioActionValue) {
    const url = this._fileService.get(fileId).href;

    if (this.currentTrackId() === fileId) {
      this._audioPlayerService.stop();

      return;
    }

    const track = {
      id: fileId,
      title: label,
      src: url,
    };

    this._audioPlayerService.load(track);
    this._audioPlayerService.play();
  }

  handleActionEdit(action: EditableAction) {
    if (this.disableControls()) {
      console.warn('Unable to edit action. Editor is locked');
      return;
    }

    this.handleActionEditDialog(action);
  }

  handleActionDelete(action: InteractionAction) {
    if (this.disableControls()) {
      console.warn('Unable to delete action. Editor is locked');
      return;
    }

    if (action.type === 'audio' && action.value.fileId === this.currentTrackId()) {
      this._audioPlayerService.stop();
    }

    this._dialog.confirm({
      title: 'Actie verwijderen?',
      message:
        'De actie die bij deze vraag hoort zal worden verwijderd. Weet je zeker dat je de actie wilt verwijderen?',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () => {
        this._interactionEditorState.emitEvent({
          entity: 'actions',
          action: 'delete',
          id: action._id,
        });

        return this._interactionEditorState.status$.pipe(
          filter((state) => state === 'idle'),
          take(1)
        );
      },
    });
  }

  handleActionAddDialog(
    type: InteractionActionType,
    toIndex?: number,
    totalActions?: number
  ) {
    if (this.disableControls()) {
      console.warn('Unable to add action. Editor is locked');
      return;
    }

    switch (type) {
      case 'speak':
        this._handleSpeakAction((result) =>
          of(result).pipe(
            filter((result) => result != null),
            tap((result) => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'create',
                index: toIndex,
                value: {
                  type: 'speak',
                  value: result,
                },
              });
            }),
            switchMap(() => this._interactionEditorState.status$),
            filter((state) => state === 'idle'),
            take(1)
          )
        );
        break;

      case 'audio':
        this._handleAudioAction((result) =>
          of(result).pipe(
            filter((result) => result != null),
            tap((result) => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'create',
                index: toIndex,
                value: {
                  type: 'audio',
                  value: result.id,
                },
              });
            }),
            switchMap(() => this._interactionEditorState.status$),
            filter((state) => state === 'idle'),
            take(1)
          )
        );
        break;
      case 'goto-question': {
        const handler: ConfirmHandler<InteractionQuestion> = (result) =>
          of(result).pipe(
            filter((result) => result != null),
            tap((result) => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'create',
                index: toIndex,
                value: {
                  type: 'goto-question',
                  value: result._id,
                },
              });
            }),
            switchMap(() => this._interactionEditorState.status$),
            filter((state) => state === 'idle'),
            take(1)
          );

        const showConfirmDialog =
          isNumber(toIndex) && isNumber(totalActions) && toIndex < totalActions;

        if (showConfirmDialog) {
          const dialogRef = this._dialog.confirm({
            title: 'Actie plaatsen?',
            message:
              'Deze actie zal alle onderliggende acties verwijderen. Weet je zeker dat je deze wilt plaatsen? Deze actie kan niet ongedaan worden gemaakt',
            confirmText: 'Begrepen',
            cancelText: 'Annuleren',
          });

          dialogRef
            .afterClosed()
            .pipe(
              tap((confirmed) => {
                if (!confirmed) {
                  return;
                }

                this._handleGotoQuestionAction(handler);
              })
            )
            .subscribe();

          return;
        }

        this._handleGotoQuestionAction(handler);

        break;
      }

      case 'goto-interaction': {
        const handler: ConfirmHandler<Interaction> = (result) =>
          of(result).pipe(
            filter((result) => result != null),
            tap((result) => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'create',
                index: toIndex,
                value: {
                  type: 'goto-interaction',
                  value: result.correlationId,
                },
              });
            }),
            switchMap(() => this._interactionEditorState.status$),
            filter((state) => state === 'idle'),
            take(1)
          );

        const showConfirmDialog =
          isNumber(toIndex) && isNumber(totalActions) && toIndex < totalActions;

        if (showConfirmDialog) {
          const dialogRef = this._dialog.confirm({
            title: 'Actie plaatsen?',
            message:
              'Deze actie zal alle onderliggende acties verwijderen. Weet je zeker dat je deze wilt plaatsen? Deze actie kan niet ongedaan worden gemaakt',
            confirmText: 'Begrepen',
            cancelText: 'Annuleren',
          });

          dialogRef
            .afterClosed()
            .pipe(
              filter((result) => result),
              tap(() => this._handleGotoInteractionAction(handler))
            )
            .subscribe();

          return;
        }

        this._handleGotoInteractionAction(handler);

        break;
      }

      case 'notification-sms':
        this._handleNotificationSMSAction((result) =>
          of(result).pipe(
            filter((result) => result != null),
            map((result) => ({
              message: result.message,
              contactIds: result.recipients.map((value) => value.id),
            })),
            tap((result) => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'create',
                index: toIndex,
                value: {
                  type: 'notification-sms',
                  value: result,
                },
              });
            }),

            switchMap(() => this._interactionEditorState.status$),
            filter((state) => state === 'idle'),
            take(1)
          )
        );
        break;

      case 'stop':
        this._handleStopAction(() => {
          this._interactionEditorState.emitEvent({
            entity: 'actions',
            action: 'create',
            index: toIndex,
            value: {
              type: 'stop',
              value: null,
            },
          });

          return this._interactionEditorState.status$.pipe(
            filter((state) => state === 'idle'),
            take(1)
          );
        });
        break;

      case 'image':
        this._handleImageAction((result) =>
          of(result).pipe(
            filter((r): r is ImageActionDialogResult => r != null),
            switchMap((r) => this._importIfNeeded(r.media, this.clientId(), this._getMediaContext()).pipe(
              switchMap((imported) => {
                this._interactionEditorState.emitEvent({
                  entity: 'actions',
                  action: 'create',
                  index: toIndex,
                  value: {
                    type: 'image',
                    value: {
                      mediaId: imported.id,
                      duration: r.duration,
                    },
                  },
                });

                return this._interactionEditorState.status$.pipe(
                  filter((state) => state === 'idle'),
                  take(1)
                );
              }),
            )),
          )
        );
        break;
    }
  }

  handleActionEditDialog(action: EditableAction) {
    switch (action.type) {
      case 'speak':
        this._handleSpeakAction(
          (result) =>
            of(result).pipe(
              filter((result) => result != null),
              tap((result) => {
                this._interactionEditorState.emitEvent({
                  entity: 'actions',
                  action: 'update',
                  id: action._id,
                  value: {
                    type: 'speak',
                    value: result,
                  },
                });
              }),
              switchMap(() => this._interactionEditorState.status$),
              filter((state) => state === 'idle'),
              take(1)
            ),
          {
            title: 'Spraakactie bewerken',
            value: action.value,
          }
        );
        break;

      case 'audio':
        // Editing an audio action is choosing another file, from the media
        // library. Adding one is offered only with `media:read`
        // (`ACTION_LIST_ITEMS`); an existing one says why it cannot change.
        if (!this._permissionService.hasPermission('media:read')) {
          this._permissionSync.explainMissing(['media:read']);
          break;
        }
        this._handleAudioAction((result) =>
          of(result).pipe(
            filter((result) => result != null),
            tap((result) => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'update',
                id: action._id,
                value: {
                  type: 'audio',
                  value: result.id,
                },
              });
            }),
            switchMap(() => this._interactionEditorState.status$),
            filter((state) => state === 'idle'),
            take(1)
          )
        );
        break;

      case 'goto-question':
        this._handleGotoQuestionAction((result) =>
          of(result).pipe(
            filter((result) => result != null),
            tap((result) => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'update',
                id: action._id,
                value: {
                  type: 'goto-question',
                  value: result._id,
                },
              });
            }),
            switchMap(() => this._interactionEditorState.status$),
            filter((state) => state === 'idle'),
            take(1)
          )
        );
        break;

      case 'goto-interaction':
        this._handleGotoInteractionAction((result) =>
          of(result).pipe(
            filter((result) => result != null),
            tap((result) => {
              this._interactionEditorState.emitEvent({
                entity: 'actions',
                action: 'update',
                id: action._id,
                value: {
                  type: 'goto-interaction',
                  value: result.correlationId,
                },
              });
            }),
            switchMap(() => this._interactionEditorState.status$),
            filter((state) => state === 'idle'),
            take(1)
          )
        );
        break;

      case 'notification-sms':
        this._handleNotificationSMSAction(
          (result) =>
            of(result).pipe(
              filter((result) => result != null),
              map((result) => ({
                message: result.message,
                contactIds: result.recipients.map((value) => value.id),
              })),
              tap((result) => {
                this._interactionEditorState.emitEvent({
                  entity: 'actions',
                  action: 'update',
                  id: action._id,
                  value: {
                    type: 'notification-sms',
                    value: result,
                  },
                });
              }),

              switchMap(() => this._interactionEditorState.status$),
              filter((state) => state === 'idle'),
              take(1)
            ),
          action.value
        );
        break;

      case 'image':
        this._handleImageAction(
          (result) =>
            of(result).pipe(
              filter((r): r is ImageActionDialogResult => r != null),
              switchMap((r) => this._importIfNeeded(r.media, this.clientId(), this._getMediaContext()).pipe(
                switchMap((imported) => {
                  this._interactionEditorState.emitEvent({
                    entity: 'actions',
                    action: 'update',
                    id: action._id,
                    value: {
                      type: 'image',
                      value: {
                        mediaId: imported.id,
                        duration: r.duration,
                      },
                    },
                  });

                  return this._interactionEditorState.status$.pipe(
                    filter((state) => state === 'idle'),
                    take(1)
                  );
                }),
              )),
            ),
          {
            selectedMedia: {
              id: action.value._id,
              label: action.value.label,
              fileId: action.value.fileId,
              fileUrl: '',
              icon: 'image',
              type: 'image',
              shareId: null,
              scope: 'unit',
            },
            duration: action.value.duration,
          },
        );
        break;
    }
  }

  private _handleSpeakAction(
    callback: ConfirmHandler<string>,
    context?: {
      title?: string;
      value?: string;
    }
  ) {
    this._dialog.prompt(
      {
        title: context?.title ?? 'Spraakactie toevoegen',
        message: '',
        confirmText: 'Opslaan',
        cancelText: 'Annuleren',
        input: {
          mode: 'textarea',
          label: 'Spraakactie',
          placeholder: 'Vul een spraakactie in',
          defaultValue: context?.value,
          autoResize: false,
          rows: 4,
          validators: {
            required: {
              validator: Validators.required,
              errorMessage: 'Vul een spraakactie in',
            },
            maxlength: {
              validator: Validators.maxLength(255),
              errorMessage: (args) => {
                const errors = args as ValidationErrors;

                return `Maximaal ${errors['requiredLength']} karakters`;
              },
            },
          },
        },
        onConfirm: callback,
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  private _handleAudioAction(callback: ConfirmHandler<MediaItem>) {
    const clientId = this.clientId();
    const context: MediaContext = clientId ? { scope: 'client', clientId } : { scope: 'unit' };

    this._mediaPickerDialog.open((result) =>
      of(result).pipe(
        filter((result) => result != null),
        switchMap((media) => this._importIfNeeded(media, clientId, context)),
        switchMap((imported) => callback(imported))
      )
    , {
      type: 'audio',
      context,
    });
  }

  private _importIfNeeded(
    mediaItem: MediaItem,
    clientId: string | null,
    context: MediaContext
  ): Observable<MediaItem> {
    if (!clientId || mediaItem.scope === 'client') {
      return of(mediaItem);
    }

    this._isImportingMedia.set(true);

    return this._mediaService.import(mediaItem.id, context).pipe(
      map((importedMedia) => ({
        ...mediaItem,
        id: importedMedia._id,
        fileId: importedMedia.fileId,
        scope: 'client' as const,
      })),
      finalize(() => this._isImportingMedia.set(false)),
      catchError((error) => {
        this._snackBar.open('Media import failed', '', { duration: 3000 });
        return throwError(() => error);
      })
    );
  }

  private _handleGotoQuestionAction(callback: ConfirmHandler<InteractionQuestion>) {
    const selectedQuestionId = this._interactionEditorState.selectedQuestionId();

    if (!selectedQuestionId) {
      console.warn('No question selected.');
      return;
    }

    const allQuestions = this.questions().sort((a, b) => a.index - b.index);

    const selectedIndex = allQuestions.findIndex(
      (question) => question._id === selectedQuestionId
    );

    const questions = allQuestions.slice(selectedIndex + 2);

    this._dialog.open(
      GotoQuestionDialogComponent,
      {
        questions: questions,
        onConfirm: callback,
      },
      {
        maxWidth: '560px',
        width: '100%',
      }
    );
  }

  private _handleGotoInteractionAction(callback: ConfirmHandler<Interaction>) {
    const currentState = this._interactionEditorState.interaction();
    const clientId = currentState?.clientId;

    this._interactionSelectionDialog.open((result) => callback(result?.[0]), {
      multiple: false,
      status: ['published'],
      mode: 'match',
      clientId,
      exclude: (value) => value.correlationId === currentState?.correlationId,
      storageKey: 'goto-interaction-selection',
    });
  }

  private _handleNotificationSMSAction(
    callback: ConfirmHandler<NotificationSMSDialogResult>,
    context?: {
      message: string;
      recipients: {
        _id: string;
        name: string;
        phoneNumber: string;
      }[];
    }
  ) {
    this._dialog.open(
      NotificationSmsDialogComponent,
      {
        ...context,
        onConfirm: callback,
      },
      {
        maxWidth: '560px',
        width: '100%',
      }
    );
  }

  private _handleStopAction(callback: ConfirmHandler) {
    this._dialog.confirm({
      title: 'Interactie stoppen',
      message:
        'Weet je zeker dat je een stop actie wilt toevoegen? Dit beëindigt de volledige interactie en verwijdert alle onderliggende acties. Deze actie kan niet ongedaan worden gemaakt.',
      confirmText: 'Opslaan',
      cancelText: 'Annuleren',
      onConfirm: callback,
    });
  }

  private _handleImageAction(
    callback: ConfirmHandler<ImageActionDialogResult>,
    context?: {
      selectedMedia?: MediaItem;
      duration?: number;
    },
  ) {
    const clientId = this.clientId();
    const mediaContext: MediaContext = clientId
      ? { scope: 'client', clientId }
      : { scope: 'unit' };

    this._dialog.open(
      ImageActionDialogComponent,
      {
        ...context,
        context: mediaContext,
        onConfirm: callback,
      },
      {
        maxWidth: '396px',
        width: '100%',
      },
    );
  }

  private _getMediaContext(): MediaContext {
    const clientId = this.clientId();
    return clientId ? { scope: 'client', clientId } : { scope: 'unit' };
  }
}
